import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../app/context/AuthContext';
import BlogForm from '../molecules/DashboardComponents/blog/BlogForm';
import BlogList from '../molecules/DashboardComponents/blog/BlogList';
import { createBlogPost } from '../../app/helper/blogService';
import { createJobPost } from '../../app/helper/jobService';
import { uploadData, uploadImages } from '../../app/helper/functions';
import { createGalleryImages } from '../../app/helper/galleryService';
import JobPostForm from '../molecules/DashboardComponents/jobPost/JobPostForm';
import JobPostList from '../molecules/DashboardComponents/jobPost/JobPostList';
import GalleryUploadForm from '../molecules/DashboardComponents/gallery/GalleryUploadForm';
import GalleryList from '../molecules/DashboardComponents/gallery/GalleryList';
import { migrate } from '../../app/firebase/firebase-migration';

const DashboardPage: React.FC = () => {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [showGalleryImage, setShowGalleryImage] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBlogSubmit = async (blogData: FormData) => {
    setIsSubmitting(true);
    setFormError('');

    try {
      const result = await createBlogPost(blogData);

      if (result.success) {
        // Show success message
        setFormSuccess(true);
        setTimeout(() => {
          setFormSuccess(false);
          setShowBlogForm(false);
        }, 3000);
      } else {
        setFormError(result.error || 'Failed to create blog post');
      }
    } catch (error) {
      setFormError((error as Error).message || 'An unexpected error occurred');
      console.error('Error submitting blog:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJobSubmit = async (jobData: FormData) => {
    setIsSubmitting(true);
    setFormError('');

    try {
      const result = await createJobPost(jobData);

      if (result.success) {
        // Show success message
        setFormSuccess(true);
        setTimeout(() => {
          setFormSuccess(false);
          setShowJobForm(false);
        }, 3000);
      } else {
        setFormError(result.error || 'Failed to create job post');
      }
    } catch (error) {
      setFormError((error as Error).message || 'An unexpected error occurred');
      console.error('Error submitting job:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGallerySubmit = async (imageUrls: string[]) => {
    setIsSubmitting(true);
    setFormError('');

    try {
      const result = await createGalleryImages(imageUrls);

      if (result.success) {
        // Show success message
        setFormSuccess(true);
        setTimeout(() => {
          setFormSuccess(false);
          setShowGalleryImage(false);
        }, 3000);
      } else {
        setFormError(result.error || 'Failed to upload gallery images');
      }
    } catch (error) {
      setFormError((error as Error).message || 'An unexpected error occurred');
      console.error('Error submitting gallery images:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-darkGreyPrimary">
      <nav className="bg-blackDarkTheme shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-white text-xl font-bold">Admin Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center">
              <div className="text-gray-300 mr-4">
                {currentUser?.email}
              </div>
              <button
                onClick={handleSignOut}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <header className="bg-blackDark shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <div className='flex gap-4'>
            <button
              onClick={() => {
                setShowGalleryImage(!showGalleryImage);
                setShowBlogForm(false);
                setShowJobForm(false);
                setFormError('');
              }}
              className="bg-blue hover:bg-yellow text-white px-4 py-2 rounded-md text-sm font-medium"
              disabled={isSubmitting}
            >
              {showGalleryImage ? 'Cancel' : 'Upload Gallery Images'}
            </button>
            <button
              onClick={() => {
                setShowJobForm(!showJobForm);
                setShowBlogForm(false);
                setShowGalleryImage(false);
                setFormError('');
              }}
              className="bg-blue hover:bg-yellow text-white px-4 py-2 rounded-md text-sm font-medium"
              disabled={isSubmitting}
            >
              {showJobForm ? 'Cancel' : 'Create Job Post'}
            </button>
            <button
              onClick={() => {
                setShowBlogForm(!showBlogForm);
                setShowJobForm(false);
                setShowGalleryImage(false);
                setFormError('');
              }}
              className="bg-blue hover:bg-yellow text-white px-4 py-2 rounded-md text-sm font-medium"
              disabled={isSubmitting}
            >
              {showBlogForm ? 'Cancel' : 'Create Blog Post'}
            </button>
            <button
              onClick={uploadImages}
              className="bg-blue hover:bg-yellow text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Image Upload
            </button>
            <button
              onClick={uploadData}
              className="bg-blue hover:bg-yellow text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Upload Data
            </button>
          </div>
        </div>
      </header>

      <main>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {formSuccess && (
            <div className="mb-6 orange-bg-100 border border-second-400 text-second-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Success!</strong>
              <span className="block sm:inline"> Your post has been published successfully.</span>
            </div>
          )}

          {formError && (
            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {formError}</span>
            </div>
          )}

          {showBlogForm ? (
            <div className="px-4 py-6 sm:px-0">
              <BlogForm onSubmit={handleBlogSubmit} />
            </div>
          ) : showJobForm ? (
            <div className="px-4 py-6 sm:px-0">
              <JobPostForm onSubmit={handleJobSubmit} />
            </div>
          ) : showGalleryImage ? (
            <div className="px-4 py-6 sm:px-0">
              <GalleryUploadForm onSubmit={handleGallerySubmit} />
            </div>
          ) : (
            <div className="px-4 py-6 sm:px-0">
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Gallery Images</h2>
                  <GalleryList />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Blog Post</h2>
                  <BlogList />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Job Posts</h2>
                  <JobPostList />
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage; 